// Consent-gated analytics (PostHog).
//
// The PostHog loader lives in public/index.html and is initialised with
// `opt_out_capturing_by_default: true` + `persistence: 'memory'`, so it never
// captures events or sets cookies on its own. This module is the single place
// that opts PostHog in/out based on the user's "analytics" cookie consent:
//   - consent granted  -> switch to cookie persistence + opt in (analytics cookies)
//   - consent declined -> opt out, reset and remove any analytics cookies
import { useEffect } from 'react';
import { useConsent } from './consent';
import api from './api';

// Google Analytics 4 measurement ID.
const GA_MEASUREMENT_ID = 'G-QETDG34XX3';
let gaScriptLoaded = false;

// Dynamically inject gtag.js the first time analytics consent is granted.
// GDPR: the remote GA script is never loaded without consent.
function loadGA() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (!gaScriptLoaded) {
    gaScriptLoaded = true;
    try {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(s);
      // IP anonymization is required for GDPR compliance.
      window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
    } catch (e) { /* no-op */ }
  }
}

// Remove GA first-party cookies (_ga, _ga_*, _gid, _gat*) on decline.
function clearGACookies() {
  try {
    const host = window.location.hostname;
    document.cookie.split(';').map((c) => c.split('=')[0].trim())
      .filter((name) => name && (name === '_ga' || name.indexOf('_ga_') === 0 || name === '_gid' || name.indexOf('_gat') === 0))
      .forEach((name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
      });
  } catch (e) { /* no-op */ }
}

// Safe event tracking. PostHog is opted out until the user consents to the
// "analytics" category, so capture() is a no-op without consent. We still guard
// defensively so tracking never throws or blocks the UI. Events are also
// forwarded to GA4 (which only records them once consent has been granted).
export function track(event, props) {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : null;
    if (ph && typeof ph.capture === 'function') {
      if (!(typeof ph.has_opted_in_capturing === 'function' && !ph.has_opted_in_capturing())) {
        ph.capture(event, props || {});
      }
    }
  } catch (e) { /* no-op */ }
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, props || {});
    }
  } catch (e) { /* no-op */ }
  // Persist export events server-side (anonymous, no cookies) so the admin
  // conversion funnel (Created -> Exported -> Shared) can measure real usage.
  // This is intentionally independent of analytics consent, mirroring the
  // anonymous share-tracking (aggregate usage statistics, legitimate interest).
  try {
    if (event === 'barcode_exported') {
      const p = props || {};
      api.recordExport({
        type: p.type || 'unknown',
        format: p.format || 'unknown',
        source: p.source || 'unknown',
      });
    }
  } catch (e) { /* no-op */ }
}

// Remove PostHog's first-party cookies (ph_<token>_posthog, plus legacy names).
function clearPostHogCookies() {
  try {
    const matches = document.cookie.split(';').map((c) => c.split('=')[0].trim());
    matches
      .filter((name) => name && (name.indexOf('ph_') === 0 || name.indexOf('__ph') === 0 || name.indexOf('posthog') !== -1))
      .forEach((name) => {
        const host = window.location.hostname;
        // Expire for the current path and for the base domain.
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
      });
  } catch (e) { /* no-op */ }
}

/**
 * Renders nothing. Watches consent and toggles PostHog accordingly.
 * Mount once inside <ConsentProvider>.
 */
export const AnalyticsGate = () => {
  const { consent } = useConsent();

  useEffect(() => {
    const analyticsAllowed = !!(consent && consent.analytics);
    const declined = !!(consent && consent.analytics === false);

    // --- PostHog ---
    const ph = typeof window !== 'undefined' ? window.posthog : null;
    if (ph && typeof ph.opt_in_capturing === 'function') {
      if (analyticsAllowed) {
        try {
          if (typeof ph.set_config === 'function') {
            ph.set_config({ persistence: 'localStorage+cookie' });
          }
          ph.opt_in_capturing();
          // Capture an initial pageview now that we are allowed to.
          if (typeof ph.capture === 'function') ph.capture('$pageview');
        } catch (e) { /* no-op */ }
      } else {
        try {
          ph.opt_out_capturing();
          if (typeof ph.set_config === 'function') {
            ph.set_config({ persistence: 'memory' });
          }
          if (typeof ph.reset === 'function') ph.reset(true);
        } catch (e) { /* no-op */ }
        // Only decisively clear cookies once the user has actively declined.
        if (declined) clearPostHogCookies();
      }
    }

    // --- Google Analytics 4 (independent of PostHog) ---
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        if (analyticsAllowed) {
          window.gtag('consent', 'update', { analytics_storage: 'granted' });
          loadGA(); // injects gtag.js once, with anonymize_ip
        } else {
          window.gtag('consent', 'update', { analytics_storage: 'denied' });
          if (declined) clearGACookies();
        }
      }
    } catch (e) { /* no-op */ }
  }, [consent]);

  return null;
};
